import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Plus, X, Clock, RotateCcw, Package, Trash2, Play, LogOut as LeaveIcon, Undo2 } from 'lucide-react';
import { jobsCol } from './database.js';
import { createJobDoc, updateOne } from './lib/db-crud.js';

import { saveTaxPreset } from './lib/use-business-profile.js';
import { isRunning, normalizeLineItems, computeJobTotal, normalizeJobTax, computeJobTax, computeGrandTotal } from './lib/job-helpers.js';
import { vehicleLabel } from './Vehicles.jsx';

import CustomerWizard from './CustomerWizard.jsx';
import JobLineItemsEditor from './JobLineItemsEditor.jsx';
import { DEFAULT_DURATION, formatTimeOfDay, combineDateAndTime, timeInputValue, dateInputValue, dateFromInputValue } from './lib/calendar-view.js';
import { CustomerPicker } from './CalendarViews.jsx';
function JobSidebar({ mode, initial, liveJobId, allLiveJobs, customers, vehicles, allJobs, laborPresets = [], taxPresets = [], partSearchLinks, onClose, onStart, onLeave, onUndoStart }) {
  const isEditing = mode === 'edit';
  const navigate = useNavigate();
  // Live job lookup re-runs on every render of the sidebar so the
  // work-state buttons reflect the latest Firestore data — the moment
  // handleStart updates startedAt, this find returns the new object
  // and the button flips to Leave.
  const job = liveJobId
    ? (allLiveJobs.find((j) => j.id === liveJobId) || null)
    : null;
  const workInProgress = isEditing && isRunning(job);
  // Show the work-action row whenever editing a real, non-terminal job.
  // Paid OR closed jobs hide it (work is done — re-starting would be
  // confusing). Anything else gets the button.
  const canShowWorkActions = isEditing && !!job && job.status !== 'paid' && !job.closedAt;

  const [customerId, setCustomerId] = useState(initial.customerId || '');
  const [vehicleId, setVehicleId] = useState(initial.vehicleId || '');
  const [description, setDescription] = useState(initial.description || '');
  // Parts/labor split — same model as Jobs.jsx JobModal. Normalize legacy
  // single-line jobs to the new shape on load.
  const [lineItems, setLineItems] = useState(() => normalizeLineItems(initial.lineItems));
  const [shopSupplies, setShopSupplies] = useState(Number(initial.shopSupplies) || 0);
  // Sales tax — same model as the JobModal on the customer page.
  const [tax, setTax] = useState(() => normalizeJobTax(initial.tax));
  const [scheduledDate, setScheduledDate] = useState(
    initial.scheduledAt ? dateInputValue(initial.scheduledAt) : dateInputValue(new Date())
  );
  const [scheduledTime, setScheduledTime] = useState(
    initial.scheduledAt ? timeInputValue(initial.scheduledAt) : '09:00'
  );
  const [duration, setDuration] = useState(String(initial.durationMinutes || DEFAULT_DURATION));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // CustomerWizard pops on top of the sidebar when user picks "+ New
  // customer" in the customer search. On finish:
  //   - If wizard created a scheduled job, close the whole sidebar
  //     (the job is in the calendar already, no need to keep the form open)
  //   - Otherwise, just select the new customer in the picker and keep
  //     the sidebar form open so user can finish entering price + details
  const [wizardOpen, setWizardOpen] = useState(false);

  // Vehicles for the currently-selected customer
  const customerVehicles = useMemo(
    () => vehicles.filter((v) => v.customerId === customerId),
    [vehicles, customerId]
  );
  const selectedVehicleLabel = vehicleLabel(customerVehicles.find((v) => v.id === vehicleId));

  // Auto-pick first vehicle when customer changes (and current pick is invalid)
  useEffect(() => {
    if (customerVehicles.length === 0) {
      if (vehicleId) setVehicleId('');
      return;
    }
    const stillValid = customerVehicles.some((v) => v.id === vehicleId);
    if (!stillValid) setVehicleId(customerVehicles[0].id);
  }, [customerId, customerVehicles, vehicleId]);

  function checkConflict(start, durMin) {
    const end = new Date(start.getTime() + durMin * 60_000);
    return allJobs.find((j) => {
      if (isEditing && j.id === initial.id) return false;
      const jEnd = new Date(j._start.getTime() + j._durationMinutes * 60_000);
      return start < jEnd && end > j._start;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!customerId) { setError('Pick a customer.'); return; }
    if (!vehicleId) { setError('Pick a vehicle. Add one on the customer page if needed.'); return; }
    const desc = description.trim();
    if (!desc) { setError('Description is required.'); return; }
    const total = computeJobTotal({ lineItems, shopSupplies });
    if (!isFinite(total) || total < 0) { setError('Total came out invalid — check the line items.'); return; }
    const dur = Number(duration);
    if (!isFinite(dur) || dur < 15) { setError('Duration must be at least 15 minutes.'); return; }

    const start = combineDateAndTime(dateFromInputValue(scheduledDate), scheduledTime);

    // Conflict warning — allow override
    const conflict = checkConflict(start, dur);
    if (conflict) {
      const cName = customers.find((c) => c.id === conflict.customerId)?.name || '(unknown)';
      const ok = confirm(
        `Heads up — this overlaps with:\n\n` +
        `${formatTimeOfDay(conflict._start)} · ${cName}\n` +
        `"${conflict.description}"\n\n` +
        `OK to schedule anyway. Cancel to pick a different time.`
      );
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      const payload = {
        description: desc,
        vehicleId,
        scheduledAt: start,
        durationMinutes: dur,
        lineItems,
        shopSupplies: Number(shopSupplies) || 0,
        totalPrice: total, // auto-computed, PRE-TAX, kept for backward compat
        // Tax sits beside the subtotal, never inside it — totalPrice
        // feeds Earnings, and tax collected is not income.
        tax: normalizeJobTax(tax),
        taxTotal: computeJobTax({ lineItems, shopSupplies, tax }).total,
        grandTotal: computeGrandTotal({ lineItems, shopSupplies, tax }),
      };
      if (isEditing) {
        await updateOne(jobsCol(), initial.id, payload);
      } else {
        await createJobDoc(jobsCol(), {
          ...payload,
          customerId,
          status: 'draft',
          shareId: null,
          invoiceShareId: null,
          sentAt: null,
          approvedAt: null,
          completedAt: null,
          paidAt: null,
        });
      }
      onClose();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  function handleOpenJobPage() {
    if (!isEditing || !customerId) return;
    onClose();
    navigate(`/customers/${customerId}`);
  }

  return (
    <div className="sidebar-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sidebar-panel" role="dialog" aria-modal="true">
        <div className="sidebar-header">
          <h2>{isEditing ? 'Edit appointment' : 'New appointment'}</h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Start / Leave actions appear at the top of the edit panel so the
            mechanic can punch in/out without scrolling past the whole form.
            Hidden on Create (the job doesn't exist yet) and on terminal jobs. */}
        {/* Work action row — always rendered when editing so the user
            can SEE why a button isn't appearing if it can't (rather than
            the whole row silently disappearing). Helps debug states
            we'd otherwise miss. */}
        {isEditing && (
          <div className="sidebar-work-actions">
            {canShowWorkActions ? (
              !workInProgress ? (
                <button type="button" className="btn hero-action-btn start" onClick={() => onStart(job)}>
                  <Play size={16} strokeWidth={2.5} /> Start job
                </button>
              ) : (
                <>
                  <button type="button" className="btn hero-action-btn leave" onClick={() => onLeave(job)}>
                    <LeaveIcon size={16} strokeWidth={2.5} /> Leave
                  </button>
                  <button
                    type="button"
                    className="hero-undo-btn"
                    onClick={() => onUndoStart(job)}
                    title="Undo Start (cancel the work timer)"
                    aria-label="Undo Start"
                  >
                    <Undo2 size={16} />
                  </button>
                </>
              )
            ) : (
              <span className="muted small" style={{ width: '100%', textAlign: 'center' }}>
                {!job
                  ? 'Loading job…'
                  : job.closedAt
                    ? 'Job is closed — view in History to reopen.'
                    : job.status === 'paid'
                      ? 'Job is paid — work complete.'
                      : 'Work actions unavailable.'}
              </span>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="form sidebar-form">
          <div className="form-row-pair">
            <label className="form-row">
              <span><Clock size={11} style={{ marginRight: 4, verticalAlign: '-1px' }} />Date</span>
              <input
                type="date" required
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
              />
            </label>
            <label className="form-row">
              <span>Start time</span>
              <input
                type="time" required step="900"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              />
            </label>
            <label className="form-row">
              <span>Duration (min)</span>
              <input
                type="number" required min={15} step={15} inputMode="numeric"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </label>
          </div>

          <div className="form-row">
            <span>Customer</span>
            <CustomerPicker
              customers={customers}
              value={customerId}
              onChange={setCustomerId}
              onCreateNew={() => setWizardOpen(true)}
            />
          </div>

          <label className="form-row">
            <span>Vehicle</span>
            <select
              required
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              disabled={!customerId || customerVehicles.length === 0}
            >
              {customerVehicles.length === 0 ? (
                <option value="">— No vehicles for this customer —</option>
              ) : (
                <>
                  <option value="">— Select a vehicle —</option>
                  {customerVehicles.map((v) => (
                    <option key={v.id} value={v.id}>{vehicleLabel(v)}</option>
                  ))}
                </>
              )}
            </select>
            {customerId && customerVehicles.length === 0 && (
              <span className="muted small" style={{ marginTop: 4 }}>
                This customer has no vehicles yet — add one from their detail page.
              </span>
            )}
          </label>

          <label className="form-row">
            <span>Description</span>
            <textarea
              required maxLength={500} rows={3} autoFocus
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Replace battery + alternator"
            />
          </label>

          <JobLineItemsEditor
            lineItems={lineItems}
            shopSupplies={shopSupplies}
            laborPresets={laborPresets}
            taxPresets={taxPresets}
            tax={tax}
            partSearchLinks={partSearchLinks}
            vehicleLabel={selectedVehicleLabel}
            onLineItemsChange={setLineItems}
            onShopSuppliesChange={setShopSupplies}
            onTaxChange={setTax}
            onSaveTaxPreset={saveTaxPreset}
          />

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            {isEditing && (
              <button type="button" className="btn-link" onClick={handleOpenJobPage}>
                Open job page
              </button>
            )}
            <button type="button" className="btn-link" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Saving…' : isEditing ? 'Save changes' : <><Plus size={14} /> Schedule</>}
            </button>
          </div>
        </form>
      </div>

      {wizardOpen && (
        <CustomerWizard
          initialSchedule={{
            date: scheduledDate,
            time: scheduledTime,
            duration: Number(duration) || DEFAULT_DURATION,
          }}
          onComplete={({ customerId: newCustomerId, jobId }) => {
            // If the wizard created its own scheduled job, the sidebar's
            // job is redundant — close everything. Otherwise just select
            // the new customer in the picker and keep the sidebar open
            // so the user can finish entering price + description.
            if (jobId) {
              onClose();
            } else {
              setCustomerId(newCustomerId);
            }
          }}
          onClose={() => setWizardOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Return-visit sidebar ────────────────────────────────────────
// Smaller sibling of JobSidebar that edits a single entry inside a
// job's returnVisits[] array. Customer + description are read-only —
// they belong to the parent job, not this individual visit. Cancel
// removes this visit from the array (and clears awaitingReturn if
// it was the last future one).
function ReturnVisitSidebar({ initial, allBlocks, jobs, onClose }) {
  const navigate = useNavigate();
  const parentJob = jobs.find((j) => j.id === initial.jobId);

  const [scheduledDate, setScheduledDate] = useState(dateInputValue(initial.scheduledAt));
  const [scheduledTime, setScheduledTime] = useState(timeInputValue(initial.scheduledAt));
  const [duration,      setDuration]      = useState(String(initial.durationMinutes || 30));
  const [waitingPart,   setWaitingPart]   = useState(initial.kind === 'parts-arrival');
  const [notes,         setNotes]         = useState(initial.notes || '');
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState(null);

  function checkConflict(start, durMin) {
    const blockKey = `${initial.jobId}::${initial.visitId}`;
    const end = new Date(start.getTime() + durMin * 60_000);
    return allBlocks.find((b) => {
      if (b._blockKey === blockKey) return false; // self
      const bEnd = new Date(b._start.getTime() + b._durationMinutes * 60_000);
      return start < bEnd && end > b._start;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const dur = Number(duration);
    if (!isFinite(dur) || dur < 15) { setError('Duration must be at least 15 minutes.'); return; }
    const start = combineDateAndTime(dateFromInputValue(scheduledDate), scheduledTime);
    if (isNaN(start.getTime())) { setError('Pick a valid date and time.'); return; }

    const conflict = checkConflict(start, dur);
    if (conflict) {
      const ok = confirm(
        `Heads up — this overlaps with another scheduled visit at ${formatTimeOfDay(conflict._start)}.\n\n`
        + `OK to schedule anyway. Cancel to pick a different time.`
      );
      if (!ok) return;
    }

    if (!parentJob) { setError('Parent job is missing.'); return; }
    const existing = Array.isArray(parentJob.returnVisits) ? parentJob.returnVisits : [];
    const updated = existing.map((v) =>
      v.id === initial.visitId
        ? { ...v, scheduledAt: start, durationMinutes: dur, kind: waitingPart ? 'parts-arrival' : 'return', notes: notes.trim() }
        : v
    );

    setSubmitting(true);
    try {
      // Recompute awaitingPart from any still-future visit waiting on parts.
      const stillAwaitingPart = updated.some((v) => v.kind === 'parts-arrival');
      await updateOne(jobsCol(), initial.jobId, {
        returnVisits: updated,
        awaitingPart: stillAwaitingPart,
      });
      onClose();
    } catch (err) {
      console.error('Update return visit failed:', err);
      setError(err.message);
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!confirm(`Remove this return visit?\n\nThe parent job stays — only this scheduled return is canceled.`)) return;
    if (!parentJob) return;
    setSubmitting(true);
    setError(null);
    try {
      const existing = Array.isArray(parentJob.returnVisits) ? parentJob.returnVisits : [];
      const remaining = existing.filter((v) => v.id !== initial.visitId);
      const patch = { returnVisits: remaining };
      // If we just removed the last return visit, the job is no longer
      // "awaiting return" — clear those flags so the chip disappears.
      if (remaining.length === 0) {
        patch.awaitingReturn = false;
        patch.awaitingPart   = false;
      } else {
        patch.awaitingPart = remaining.some((v) => v.kind === 'parts-arrival');
      }
      await updateOne(jobsCol(), initial.jobId, patch);
      onClose();
    } catch (err) {
      console.error('Cancel return visit failed:', err);
      setError(err.message);
      setSubmitting(false);
    }
  }

  function handleOpenJobPage() {
    if (!parentJob) return;
    onClose();
    navigate(`/customers/${parentJob.customerId}`);
  }

  return (
    <div className="sidebar-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sidebar-panel" role="dialog" aria-modal="true">
        <div className="sidebar-header">
          <h2>
            {initial.kind === 'parts-arrival' ? <Package size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--accent)' }} /> : <RotateCcw size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--accent)' }} />}
            {initial.kind === 'parts-arrival' ? 'Parts arrival' : 'Return visit'}
          </h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="form sidebar-form">
          <div className="return-visit-context">
            <div className="return-visit-customer">{initial.customerName || '(deleted customer)'}</div>
            <div className="muted small">{initial.description}</div>
          </div>

          <div className="form-row-pair">
            <label className="form-row">
              <span><Clock size={11} style={{ marginRight: 4, verticalAlign: '-1px' }} />Date</span>
              <input
                type="date" required
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
              />
            </label>
            <label className="form-row">
              <span>Start time</span>
              <input
                type="time" required step="900"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              />
            </label>
            <label className="form-row">
              <span>Duration (min)</span>
              <input
                type="number" required min={15} step={15} inputMode="numeric"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </label>
          </div>

          <label className="checkbox-row" style={{ padding: '6px 0' }}>
            <input
              type="checkbox"
              checked={waitingPart}
              onChange={(e) => setWaitingPart(e.target.checked)}
            />
            <span>
              <Package size={14} style={{ verticalAlign: '-2px', marginRight: 4, color: 'var(--accent)' }} />
              Waiting on a part to arrive
            </span>
          </label>

          <label className="form-row">
            <span>Notes (optional)</span>
            <textarea
              rows={2} maxLength={500}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Bringing the new alternator from O'Reilly"
            />
          </label>

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="btn-link" onClick={handleOpenJobPage}>
              Open job page
            </button>
            <button type="button" className="btn-link danger" onClick={handleCancel} disabled={submitting}>
              <Trash2 size={14} /> Cancel return
            </button>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { JobSidebar, ReturnVisitSidebar };
